/*
 * SCOPE-PREFIXED SOURCE IDS — how a tile names where its values come from.
 *
 * The tile plane grew up controller-shaped: the widget declaration, the
 * capability grant, the share snapshot and the fanout all key on a
 * "controllerId" string. Meanwhile a board's sources are four kinds — PLC,
 * hardware controller, statechart, integration — and the Sources tab showed a
 * model's values live while the tile beside it rendered its placeholder,
 * because nothing on the tile path could say "this id is a model".
 *
 * The fix is a NAMING convention, not a new plane: a source id is
 *
 *   <controllerId>              a controller, exactly as before
 *   model:<modelId>             a statechart's published variables
 *   integration:<integrationId> an integration's observed paths
 *
 * Every layer that treats the id as an opaque string — the grant's exact-match
 * reads, planSubscriptions' grouping, the share snapshot, the worker's
 * idempotent declaration — keeps working VERBATIM, which is the entire point:
 * the alternative (a parallel {kind, id} field threaded through five layers and
 * two serialised formats) is a wire change everybody pays for and a merge
 * hazard in every shared structure. Only the two ENDS change: the worker
 * resolves a tile's source from its config, and the host splits the prefix to
 * pick the stream.
 *
 * WRITES DELIBERATELY DO NOT RESOLVE. writeRoute() looks the id up against the
 * grant's plugin-routed writes; a prefixed id matches no plugin and the write
 * is refused — which is correct today (model variables are set via commands,
 * integration writes have their own gated verb) and safely wrong tomorrow: a
 * refusal, never a misroute.
 */

export type SourceKind = 'controller' | 'model' | 'integration';

/** Split a source id into what to subscribe and how. Bare ids are controllers. */
export function splitSourceId(id: string): { kind: SourceKind; id: string } {
  if (id.startsWith('model:')) return { kind: 'model', id: id.slice('model:'.length) };
  if (id.startsWith('integration:')) return { kind: 'integration', id: id.slice('integration:'.length) };
  return { kind: 'controller', id };
}

/*
 * Resolve what a widget's sub() call meant.
 *
 * The widgets in the catalog all write `ctx.sub(ctx.config.controllerId, …)`,
 * and a model-bound tile has no controllerId — so the call arrives with
 * undefined, which used to become a subscription to nothing and a tile stuck on
 * its placeholder. The worker HOLDS the tile's config, so it can answer what
 * the widget could not: an explicit id (prefixed or bare) is honoured verbatim,
 * an ABSENT one falls back to the config's own source, in the same
 * mutually-exclusive order the server's value scope uses (model, integration,
 * controller).
 *
 * Resolving here rather than in each widget is the structural choice: nine
 * catalog widgets and every marketplace widget already published work with
 * model and integration tiles WITHOUT an edit, and a future widget cannot get
 * the fallback wrong because it does not implement it.
 */
export function resolveSourceId(explicit: unknown, config: Record<string, unknown>): string {
  if (typeof explicit === 'string' && explicit !== '') return explicit;
  const model = config['modelId'];
  if (typeof model === 'string' && model !== '') return `model:${model}`;
  const integration = config['integrationId'];
  if (typeof integration === 'string' && integration !== '') return `integration:${integration}`;
  const controller = config['controllerId'];
  if (typeof controller === 'string' && controller !== '') return controller;
  return '';
}
